import { formatDate, Table, theme, useToast } from '@arnold/common';
import { SurveyQuery, useSendNewReportsMutation } from '../../generated/hooks';
import BaseModal, { WIDTH } from './BaseModal';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import styled from 'styled-components';

interface IProps {
  surveys: SurveyQuery['survey'][];
  onClose: () => void;
  respondentId: string;
  respondentName: string;
}

type SurveyData = {
  id: string;
  name: string;
  date: string;
};

const ContentWrapper = styled.div`
  max-height: 200px;
  overflow: auto;
  margin-bottom: ${theme.spacing.h};
`;

const SendNewReportsModal = ({ surveys, onClose, respondentId, respondentName }: IProps) => {
  const [t] = useTranslation('updateRespondent');
  const [selectedSurveys, setSelectedSurveys] = useState<string[]>([]);
  const { addToast } = useToast();

  const [sendNewReportsMutation] = useSendNewReportsMutation({
    onCompleted: () => {
      onClose();
      addToast(t('sendNewReportsSuccess'));
    },
    onError: () => {
      addToast(t('sendNewReportsError'));
    },
  });

  const toggleSurvey = (id: string | string[]) => {
    if (Array.isArray(id)) {
      setSelectedSurveys(id);
      return;
    }

    const selectedIds = selectedSurveys.slice();
    const surveyIndex = selectedSurveys.indexOf(id);
    if (surveyIndex !== -1) {
      selectedIds.splice(surveyIndex, 1);
    } else {
      selectedIds.push(id);
    }

    setSelectedSurveys(selectedIds);
  };
  return (
    <BaseModal
      title={t('sendNewReportsModalTitle', { respondentName })}
      description={t('sendNewReportsModalDescription')}
      secondaryActionText={t('cancel')}
      onSecondaryAction={onClose}
      primaryActionText={t('send')}
      onPrimaryAction={() => {
        sendNewReportsMutation({ variables: { respondentId, surveyIds: selectedSurveys } });
      }}
      onClose={onClose}
      width={WIDTH.MEDIUM}
    >
      <ContentWrapper>
        <Table<SurveyData>
          labels={{ emptyTableLabel: t('emptyTableLabel') }}
          columns={[
            {
              key: 'name',
              title: t('name'),
              dataIndex: 'name',
            },
            {
              key: 'date',
              title: t('date'),
              dataIndex: 'date',
            },
          ]}
          dataSources={surveys
            .slice()
            .sort((a, b) => {
              return new Date(b?.sendAt).getTime() - new Date(a?.sendAt).getTime();
            })
            .map((survey) => ({
              id: survey!.id,
              name: survey!.topicGroup?.name || '',
              date: formatDate(survey?.sendAt) + ' - ' + formatDate(survey?.endsAt || ''),
            }))}
          rowSelection={{
            selected: selectedSurveys,
            onChange: toggleSurvey,
          }}
        />
      </ContentWrapper>
    </BaseModal>
  );
};

export default SendNewReportsModal;
